import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { paymentsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  CreditCard,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Download,
  Filter,
  X,
  Calendar,
  DollarSign,
  Globe,
  Shield,
  RotateCcw,
} from 'lucide-react';

import { toast } from 'sonner';

const formatAmount = (amount, currency = 'KZT') => {
  if (!amount) return '0';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency === 'KZT' ? 'KZT' : currency,
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (date, language) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString(language === 'en' ? 'en-US' : language === 'kz' ? 'kk-KZ' : 'ru-RU');
};

const StatusBadge = ({ status, t }) => {
  const statusConfig = {
    approved: { label: t ? t('payments.approved') : 'Approved', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    declined: { label: t ? t('payments.declined') : 'Declined', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
    pending: { label: t ? t('payments.processing') : 'Processing', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    refunded: { label: t ? t('payments.refunded') : 'Refunded', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    cancelled: { label: t ? t('common.cancel') : 'Cancelled', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
    error: { label: t ? t('common.error') : 'Error', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
    init: { label: 'init', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    expired: { label: 'expired', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  };
  const config = statusConfig[status] || { label: status || 'N/A', className: 'bg-slate-500/20 text-slate-400' };
  return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
};

const PaymentsPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [error, setError] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);

  const canRefund = hasPermission('payments', 'refund');

  // Filter form state
  const [filterForm, setFilterForm] = useState({
    payment_id: '',
    status: 'all',
    merchant_key: '',
    amount_min: '',
    amount_max: '',
    currency: 'all',
    card_mask: '',
    order_number: '',
  });

  const fetchPayments = async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        page_size: pagination.pageSize,
        ...Object.fromEntries(
          Object.entries(currentFilters).filter(([_, v]) => v !== '' && v !== null && v !== 'all')
        ),
      };
      
      const response = await paymentsAPI.search(params);
      setPayments(response.data.payments || []);
      setPagination({
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
        totalPages: response.data.total_pages,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentDetails = async (paymentId) => {
    try {
      const response = await paymentsAPI.get(paymentId);
      setSelectedPayment(response.data);
    } catch (err) {
      console.error('Error fetching payment details:', err);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleSearch = () => {
    const newFilters = { ...filterForm };
    setFilters(newFilters);
    fetchPayments(1, newFilters);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilterForm({
      payment_id: '',
      status: '',
      merchant_key: '',
      amount_min: '',
      amount_max: '',
      currency: '',
      card_mask: '',
      order_number: '',
    });
    setFilters({});
    fetchPayments(1, {});
  };

  const handlePageChange = (newPage) => {
    fetchPayments(newPage);
  };

  const handleRefund = async (paymentId, amount) => {
    if (!window.confirm(`Вы уверены, что хотите сделать возврат${amount ? ` на сумму ${formatAmount(amount, 'KZT')}` : ' полной суммы'}?`)) {
      return;
    }
    
    try {
      setRefundLoading(true);
      const response = await paymentsAPI.refund(paymentId, amount);
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedPayment(null);
        fetchPayments(pagination.page);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка возврата');
    } finally {
      setRefundLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {t('payments.title')}
          <p className="text-slate-400 mt-1">
            {t('common.total')}: {pagination.total.toLocaleString(language === 'en' ? 'en-US' : 'ru-RU')} {t('payments.title').toLowerCase()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            {t('common.filter')}
            {Object.values(filters).filter(Boolean).length > 0 && (
              <Badge className="ml-2 bg-emerald-500">{Object.values(filters).filter(Boolean).length}</Badge>
            )}
          </Button>
          <Button
            onClick={async () => {
              try {
                const response = await api.get("/export/payments", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `payments_${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success("Экспорт платежей завершён");
              } catch (err) {
                toast.error("Ошибка экспорта");
              }
            }}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="export-payments-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Экспорт
          </Button>
          <Button
            onClick={() => fetchPayments(pagination.page)}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
          {error}
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white">{t("common.filter")}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">{t('payments.id')}</Label>
                <Input
                  placeholder="Например: 7991612"
                  value={filterForm.payment_id}
                  onChange={(e) => setFilterForm({ ...filterForm, payment_id: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  data-testid="filter-payment-id"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t("common.status")}</Label>
                <Select
                  value={filterForm.status}
                  onValueChange={(value) => setFilterForm({ ...filterForm, status: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue placeholder="Все статусы" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="approved">{t('common.success')}</SelectItem>
                    <SelectItem value="declined">Отклонен</SelectItem>
                    <SelectItem value="pending">В обработке</SelectItem>
                    <SelectItem value="refunded">Возврат</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Мин. сумма</Label>
                <Input
                  type="number"
                  placeholder="От"
                  value={filterForm.amount_min}
                  onChange={(e) => setFilterForm({ ...filterForm, amount_min: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Макс. сумма</Label>
                <Input
                  type="number"
                  placeholder="До"
                  value={filterForm.amount_max}
                  onChange={(e) => setFilterForm({ ...filterForm, amount_max: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t('terminals.currency')}</Label>
                <Select
                  value={filterForm.currency}
                  onValueChange={(value) => setFilterForm({ ...filterForm, currency: value })}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue placeholder="Все валюты" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="KZT">KZT</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="RUB">RUB</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Маска карты</Label>
                <Input
                  placeholder="****1234"
                  value={filterForm.card_mask}
                  onChange={(e) => setFilterForm({ ...filterForm, card_mask: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t('payments.orderNumber')}</Label>
                <Input
                  placeholder="Order number"
                  value={filterForm.order_number}
                  onChange={(e) => setFilterForm({ ...filterForm, order_number: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Ключ мерчанта</Label>
                <Input
                  placeholder="Начало ключа"
                  value={filterForm.merchant_key}
                  onChange={(e) => setFilterForm({ ...filterForm, merchant_key: e.target.value })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700">
              <Button
                onClick={handleSearch}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="apply-filters-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                {t('common.apply')}
              </Button>
              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {t('common.reset')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <CreditCard className="w-12 h-12 mb-4 opacity-50" />
              <p>{t("common.noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">ID</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t("common.status")}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t("payments.amount")}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t("payments.card")}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t("payments.gateway")}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t("common.date")}</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr
                      key={payment.id}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="p-4">
                        <span className="text-white font-medium">#{payment.id}</span>
                      </td>
                      <td className="p-4">
                        <StatusBadge status={payment.status} t={t} />
                      </td>
                      <td className="p-4">
                        <span className="text-white font-medium">
                          {formatAmount(payment.amount, payment.currency)}
                        </span>
                        <span className="text-slate-500 text-xs ml-1">{payment.currency}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300 font-mono text-sm">
                          {payment.card_masked_number || 'N/A'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300 text-sm">
                          {payment.gateway_alias || 'N/A'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-400 text-sm">
                          {formatDate(payment.created_at, language)}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchPaymentDetails(payment.id)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`view-payment-${payment.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Страница {pagination.page} из {pagination.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Detail Dialog */}
      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              Платеж #{selectedPayment?.id}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Детальная информация о платеже
            </DialogDescription>
          </DialogHeader>

          {selectedPayment && (
            <div className="space-y-4 mt-4">
              {/* Status & Amount */}
              <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                <div>
                  <p className="text-sm text-slate-400">{t("common.status")}</p>
                  <StatusBadge status={selectedPayment.status} t={t} />
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">{t("payments.amount")}</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {formatAmount(selectedPayment.amount, selectedPayment.currency)}
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">{t("payments.card")}</p>
                  <p className="text-sm text-white font-mono">{selectedPayment.card_masked_number || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">{t("payments.gateway")}</p>
                  <p className="text-sm text-white">{selectedPayment.gateway_alias || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">{t('payments.orderNumber')}</p>
                  <p className="text-sm text-white">{selectedPayment.order_number || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Тип операции</p>
                  <p className="text-sm text-white">{selectedPayment.operation_type || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">{t('audit.ip')}</p>
                  <p className="text-sm text-white font-mono">{selectedPayment.ip || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Платформа</p>
                  <p className="text-sm text-white">{selectedPayment.platform || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Страна (IP)</p>
                  <p className="text-sm text-white">{selectedPayment.country_code_by_IP || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Страна (BIN)</p>
                  <p className="text-sm text-white">{selectedPayment.country_code_by_BIN || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">3DS</p>
                  <p className="text-sm text-white">{selectedPayment.three_ds ? 'Да' : 'Нет'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Secure Code</p>
                  <p className="text-sm text-white">{selectedPayment.secure_code ? 'Да' : 'Нет'}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="border-t border-slate-700 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Создан</p>
                    <p className="text-sm text-white">{formatDate(selectedPayment.created_at, language)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Обработан</p>
                    <p className="text-sm text-white">{formatDate(selectedPayment.processed_at, language)}</p>
                  </div>
                </div>
              </div>

              {/* Declination reason */}
              {selectedPayment.declination_reason && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-xs text-red-400 mb-1">Причина отклонения</p>
                  <p className="text-sm text-red-300">{selectedPayment.declination_reason}</p>
                </div>
              )}

              {/* Refund info */}
              {selectedPayment.available_for_refund && selectedPayment.status === 'approved' && canRefund && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-xs text-emerald-400 mb-2">Доступно для возврата</p>
                  <p className="text-sm text-emerald-300 mb-3">
                    {formatAmount(selectedPayment.available_for_refund, selectedPayment.currency)}
                  </p>
                  <Button
                    onClick={() => handleRefund(selectedPayment.id)}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    disabled={refundLoading}
                    data-testid="refund-btn"
                  >
                    {refundLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RotateCcw className="w-4 h-4 mr-2" />
                    )}
                    Оформить возврат
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsPage;
